import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Phone } from 'lucide-react';
import api from '../utils/api';

export default function RatesScreen() {
  const [rateSettings, setRateSettings] = useState(null);
  const [altynBalance, setAltynBalance] = useState(null);
  const [merchantPhones, setMerchantPhones] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled([
        api.getRateSettings(),
        api.getAltynBalance(),
        api.getMerchantPhones(),
      ]);

      if (results[0].status === 'fulfilled') setRateSettings(results[0].value);
      if (results[1].status === 'fulfilled') setAltynBalance(results[1].value);
      if (results[2].status === 'fulfilled') setMerchantPhones(results[2].value);

      if (results.every(r => r.status === 'rejected')) {
        throw results[0].reason;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatNum = (n) => {
    if (n === null || n === undefined) return '—';
    return Number(n).toLocaleString('ru-RU', { maximumFractionDigits: 4 });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Курсы и Altyn</h2>
        <button
          onClick={loadData}
          disabled={loading}
          className="p-2 bg-slate-800 rounded-lg border border-slate-700 text-slate-400 hover:text-white disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Rate settings */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
          <h3 className="text-sm font-medium text-white mb-4">Настройки курса</h3>
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-5 bg-slate-700 rounded animate-pulse" />
              ))}
            </div>
          ) : rateSettings ? (
            <div className="space-y-3">
              {Object.entries(rateSettings).map(([key, value]) => {
                if (typeof value === 'object' && value !== null) return null;
                return (
                  <div key={key} className="flex justify-between items-center">
                    <span className="text-sm text-slate-400">{key}</span>
                    <span className="text-sm text-white font-medium">{String(value)}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">Нет данных</p>
          )}
        </div>

        {/* Altyn balance */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
          <h3 className="text-sm font-medium text-white mb-4">Баланс Altyn</h3>
          {loading ? (
            <div className="h-16 bg-slate-700 rounded animate-pulse" />
          ) : altynBalance ? (
            <div className="space-y-3">
              {Object.entries(altynBalance).map(([key, value]) => {
                if (typeof value === 'object' && value !== null) return null;
                return (
                  <div key={key} className="flex justify-between items-center">
                    <span className="text-sm text-slate-400">{key}</span>
                    <span className="text-sm text-white font-medium">
                      {typeof value === 'number' ? formatNum(value) : String(value)}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">Нет данных</p>
          )}
        </div>

        {/* Merchant phones */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 md:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <Phone size={16} className="text-slate-400" />
            <h3 className="text-sm font-medium text-white">Телефоны мерчанта</h3>
          </div>
          {loading ? (
            <div className="h-12 bg-slate-700 rounded animate-pulse" />
          ) : merchantPhones ? (
            <div className="space-y-2">
              {(merchantPhones.phones || merchantPhones.data || []).map((phone, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-700/30 rounded-lg p-3">
                  <span className="text-sm text-white font-mono">{phone.phone || phone.number || phone}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    phone.verified || phone.isVerified
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {phone.verified || phone.isVerified ? 'Верифицирован' : 'Не верифицирован'}
                  </span>
                </div>
              ))}
              {(!merchantPhones.phones && !merchantPhones.data) && (
                <div className="text-sm text-slate-400">
                  {JSON.stringify(merchantPhones, null, 2)}
                </div>
              )}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">Нет данных</p>
          )}
        </div>
      </div>
    </div>
  );
}
